<template>
    <BaseModal
        :is-open="isOpen"
        :is-loading="isStatusLoading"
        @modal-close="closeModal"
        identifier="register-payment-flow-modal"
        keep-alive-views="RegisterPlanSelectConversionMinutesView"
        :transition-name="transitionName"
    ></BaseModal>
</template>

<script>
import { eventBus } from '../../../../lib/event-bus/event-bus';
import { EVENT_USER_MODAL_CLOSED, EVENT_USER_MODAL_OPENED } from './event';
import {
    testIsNonEmptyString,
    testIsNotNull,
    testIsNull,
    testIsTrueish,
} from '../../../../lib/test-and-assert/test-base';
import { pageStoreMixin } from '../../../store/page-store';
import DataStatus from '../../../../qaamgo/modules/sat/components/mixins/DataStatus';
import LoadingAnimation from '../../../../qaamgo/modules/sat/components/LoadingAnimation';
import { userManager } from '../../user-manager';
import { satGlobals } from '../../../../qaamgo/sat/helper/sat-globals';
import { userModalRoute } from './router';
import { getLocationHashWithoutModalPageFragment } from './modal-fragment-helper';
import { projectHelper } from '../../../misc/project-helper';
import BaseModal from '../BaseModal';

export default {
    name: 'UserModal',
    components: { LoadingAnimation, BaseModal },
    mixins: [DataStatus, pageStoreMixin],
    created() {
        this.$router.afterEach((to, from) => {
            // change slide animation direction when changing view away from the contactus form
            if (from.name === userModalRoute.CONTACT_US_FORM) {
                this.transitionName = 'slide-fade-right';

                return;
            }
            if (
                from.name == userModalRoute.REGISTER_CHOOSE_BILLING &&
                to.name == userModalRoute.REGISTER_PLAN_CREDITS_SELECT
            ) {
                this.transitionName = 'slide-fade-right';
                return;
            }
            this.transitionName = 'slide-fade-left';
        });
    },
    beforeMount() {
        window.addEventListener('beforeunload', this.onBeforeUnload);

        userManager.getUser().then((user) => {
            let currency = 'usd';

            if (testIsTrueish(user.getExtraKey('europe'))) {
                currency = 'eur';
            }

            if (satGlobals.isRegionalPricingEnabled()) {
                currency = user.getExtraKey('region_currency');
            }

            this.store.userPaymentModal.setCurrency(currency);

            const isUserEligibleForTrial = user.isAnonymous() || user.isEligibleForFreeTrial();
            const offerTrial = satGlobals.getEnableFreeTrial() && isUserEligibleForTrial;
            const isSale = satGlobals.getIsSale();

            this.store.userPaymentModal.setOfferTrial(offerTrial);

            this.store.userPaymentModal.setIsSale(isSale);

            this.setStatusDone();
        });
    },
    mounted() {
        this.registerEvents();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    },
    computed: {
        openPage() {
            return this.store.userPaymentModal.openPage;
        },
        isOpen() {
            return this.openPage !== null;
        },
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.isSubmitting;
        },
        /** @return {User} */
        storeUser() {
            return this.store.user.getUser;
        },
        /** @return {Boolean} */
        skipPlanSelect() {
            return this.store.userPaymentModal.skipPlanSelect;
        },
    },
    watch: {
        openPage: function (page, pageOld) {
            if (testIsNotNull(page) && testIsNotNull(pageOld)) {
                // skip if we switch from one modal page to another
                return;
            }

            // if the new page is null the modal is closing
            if (testIsNull(page)) {
                eventBus.emit(EVENT_USER_MODAL_CLOSED, this.$router.currentRoute);

                // make sure the modal is restored to its default state
                this.store.userPaymentModal.resetDefaults();

                return;
            }

            // if the old page is null and the new page is a string the modal is opening
            if (testIsNull(pageOld) && testIsNonEmptyString(page)) {
                window.location.hash = getLocationHashWithoutModalPageFragment();

                let openPage = this.getFallbackPageIfNeeded(page);

                if (this.$router.currentRoute?.name !== openPage) {
                    this.$router.push({ name: openPage });
                }

                eventBus.emit(EVENT_USER_MODAL_OPENED, this.$router.currentRoute);
            }
        },
    },
    methods: {
        onBeforeUnload() {
            // to prevent users being stuck in an invalid state we need to set submitting to false before unloading
            this.store.userPaymentModal.setSubmitting(false);
        },
        getFallbackPageIfNeeded(openPage) {
            /** @type {?String} */
            let fallbackPage = null;

            let fallbackRoutes = [];

            if (this.storeUser.isAnonymous()) {
                // anonymous users should not be able to directly open the modal on these pages:
                fallbackRoutes = [
                    userModalRoute.REGISTER_PLAN_SELECT,
                    userModalRoute.REGISTER_PLAN_CREDITS_SELECT,
                    userModalRoute.REGISTER_CHOOSE_BILLING,
                    userModalRoute.REGISTER_CHOOSE_PAYMENT,
                    userModalRoute.REGISTER_PAYMENT_SUCCESS,
                    userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT,
                    userModalRoute.REGISTER_PRODUCT_CREDITS_SELECT,
                    userModalRoute.CLAIM_EDUCATION,
                ];

                fallbackPage = userModalRoute.REGISTER;
            } else if (this.storeUser.isRegistered()) {
                // registered users should not be able to directly open the modal on these pages:
                fallbackRoutes = [
                    userModalRoute.LOGIN,
                    userModalRoute.REGISTER,
                    userModalRoute.REGISTER_PAYMENT_SUCCESS,
                    userModalRoute.REGISTER_CHOOSE_PAYMENT,
                    userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT,
                    userModalRoute.CLAIM_EDUCATION,
                ];

                if (satGlobals.isMultipleProductsEnabled()) {
                    fallbackPage = userModalRoute.REGISTER_PLAN_CREDITS_SELECT;
                } else {
                    fallbackPage = userModalRoute.REGISTER_PLAN_SELECT;
                }

                if (this.skipPlanSelect) {
                    fallbackPage = userModalRoute.REGISTER_CHOOSE_BILLING;
                } else {
                    fallbackRoutes.push(userModalRoute.REGISTER_CHOOSE_BILLING);
                }

                // TODO add logic to handle edu users that need to reconfirm
                // handle educational user
                if (this.storeUser.isEligibleForEducation() && !this.storeUser.isEducational()) {
                    if (this.storeUser.isEnabled()) {
                        fallbackRoutes = [
                            userModalRoute.LOGIN,
                            userModalRoute.REGISTER,
                            userModalRoute.REGISTER_PAYMENT_SUCCESS,
                            userModalRoute.REGISTER_CHOOSE_PAYMENT,
                            userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT,
                            userModalRoute.REGISTER_PLAN_SELECT_CONVERSION_MINUTES,
                            userModalRoute.REGISTER_PLAN_SELECT,
                            userModalRoute.REGISTER_PLAN_CREDITS_SELECT,
                        ];

                        fallbackPage = userModalRoute.CLAIM_EDUCATION;
                    } else {
                        fallbackRoutes = [
                            userModalRoute.LOGIN,
                            userModalRoute.REGISTER,
                            userModalRoute.REGISTER_PAYMENT_SUCCESS,
                            userModalRoute.REGISTER_CHOOSE_PAYMENT,
                            userModalRoute.CLAIM_EDUCATION,
                            userModalRoute.REGISTER_PLAN_SELECT_CONVERSION_MINUTES,
                            userModalRoute.REGISTER_PLAN_SELECT,
                            userModalRoute.REGISTER_PLAN_CREDITS_SELECT,
                        ];

                        fallbackPage = userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT;
                    }
                }

                if (projectHelper.isApi2Convert()) {
                    // registered api2convert users should not be able to directly open the modal on these pages:
                    fallbackRoutes = [
                        userModalRoute.LOGIN,
                        userModalRoute.REGISTER,
                        userModalRoute.REGISTER_PLAN_SELECT_CONVERSION_MINUTES,
                        userModalRoute.REGISTER_PLAN_SELECT,
                        userModalRoute.REGISTER_CHOOSE_PAYMENT,
                        userModalRoute.REGISTER_PAYMENT_SUCCESS,
                        userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT,
                        userModalRoute.CLAIM_EDUCATION,
                    ];

                    fallbackPage = userModalRoute.REGISTER_CHOOSE_BILLING;
                }

                if (satGlobals.isCreditsEnabled() && satGlobals.isMultipleProductsEnabled()) {
                    // if credits with multiple products are enabled show correct plan select page
                    fallbackRoutes = [
                        userModalRoute.LOGIN,
                        userModalRoute.REGISTER,
                        userModalRoute.REGISTER_PLAN_SELECT_CONVERSION_MINUTES,
                        userModalRoute.REGISTER_PLAN_SELECT,
                        userModalRoute.REGISTER_CHOOSE_PAYMENT,
                        userModalRoute.REGISTER_PAYMENT_SUCCESS,
                        userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT,
                        userModalRoute.CLAIM_EDUCATION,
                    ];

                    fallbackPage = userModalRoute.REGISTER_PLAN_CREDITS_SELECT;

                    if (this.storeUser.isPremium()) {
                      fallbackPage = userModalRoute.REGISTER_CHOOSE_BILLING;
                    }
                }

                if (satGlobals.isCreditsInAllSatsEnabled()) {
                    // if new credits are enabled show correct plan select page
                    fallbackRoutes = [
                        userModalRoute.LOGIN,
                        userModalRoute.REGISTER,
                        userModalRoute.REGISTER_PLAN_SELECT_CONVERSION_MINUTES,
                        userModalRoute.REGISTER_PLAN_SELECT,
                        userModalRoute.REGISTER_PAYMENT_SUCCESS,
                        userModalRoute.REGISTER_CONFIRMATION_MAIL_SENT,
                        userModalRoute.CLAIM_EDUCATION,
                    ];

                    fallbackPage = userModalRoute.REGISTER_CHOOSE_PAYMENT;

                    // if (this.storeUser.isPremium()) {
                    //   fallbackPage = userModalRoute.REGISTER_CHOOSE_BILLING;
                    // }
                }
            }

            if (testIsNonEmptyString(fallbackPage) && fallbackRoutes.includes(openPage)) {
                this.store.userPaymentModal.setOpenPage(fallbackPage);

                return fallbackPage;
            }

            return openPage;
        },
        registerEvents() {
            this.$router.afterEach((to, from) => {
                if (!testIsNonEmptyString(from?.name)) {
                    // do not commit page change if modal opens
                    return;
                }

                this.store.userPaymentModal.changePage({
                    fromPage: from.name,
                    toPage: to.name,
                });
            });
        },
        closeModal() {
            this.store.userPaymentModal.close();
        },
    },
    data() {
        return {
            transitionName: 'slide-fade-left',
        };
    },
};
</script>
